import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Mail, KeyRound, ArrowRight, RefreshCw, X, AlertCircle, CheckCircle2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

export default function OtpLoginModal({ isOpen, onClose, initialEmail = "", onSuccess }) {
  const { sendLoginOtp, verifyLoginOtp, user } = useAuth();
  const [email, setEmail] = useState(initialEmail);
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("REQUEST");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [statusMessage, setStatusMessage] = useState(null);

  useEffect(() => {
    if (initialEmail) {
      setEmail(initialEmail);
    }
  }, [initialEmail]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  if (!isOpen) return null;

  const handleSendOtp = async (e) => {
    if (e) e.preventDefault();
    setStatusMessage(null);

    const cleanEmail = email.trim().toLowerCase();
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!cleanEmail || !emailRegex.test(cleanEmail)) {
      setStatusMessage({ type: "error", text: "Please enter a valid email address." });
      return;
    }

    setLoading(true);
    try {
      const data = await sendLoginOtp(cleanEmail);
      const msg = data?.message || "A 6-digit code has been sent to your email.";
      setStatusMessage({ type: "success", text: msg });
      toast.success(msg);
      setStep("VERIFY");
      setCooldown(30);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to send code. Please check your email and try again.";
      setStatusMessage({ type: "error", text: detail });
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setStatusMessage(null);

    const cleanEmail = email.trim().toLowerCase();
    const cleanOtp = otp.trim();

    if (!cleanOtp || cleanOtp.length !== 6) {
      setStatusMessage({ type: "error", text: "Please enter the 6-digit verification code." });
      return;
    }

    setLoading(true);
    try {
      const data = await verifyLoginOtp(cleanEmail, cleanOtp);
      setStatusMessage({ type: "success", text: "Verified successfully. Logging in..." });
      toast.success("Signed in successfully!");

      if (onSuccess) onSuccess(data?.user || user);
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Invalid or expired code. Please try again.";
      setStatusMessage({ type: "error", text: detail });
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl border border-slate-200 p-6 sm:p-8 overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-slate-900 via-blue-700 to-slate-900"></div>

        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-900 hover:bg-slate-100 rounded-full transition-all"
        >
          <X className="w-5 h-5" />
        </button>

        <h3 className="text-xl font-black text-slate-900 tracking-tight">
          {step === "REQUEST" ? "Sign In with Email OTP" : "Enter Verification Code"}
        </h3>
        <p className="text-xs text-slate-500 font-medium mt-1 mb-5">
          {step === "REQUEST"
            ? "Receive a 6-digit one-time passcode directly to your inbox."
            : `Enter the code sent to ${email}`}
        </p>

        {statusMessage && (
          <div
            className={`mb-4 p-3 rounded-xl border text-xs font-semibold flex items-start gap-2.5 animate-in fade-in duration-200 ${
              statusMessage.type === "success"
                ? "bg-blue-50 border-blue-200 text-blue-900"
                : "bg-red-50 border-red-200 text-red-700"
            }`}
          >
            {statusMessage.type === "success" ? (
              <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
            )}
            <span>{statusMessage.text}</span>
          </div>
        )}

        {step === "REQUEST" ? (
          <form onSubmit={handleSendOtp} noValidate className="space-y-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-700 ml-1">
                Your Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                  <Mail className="w-5 h-5" />
                </div>
                <input
                  type="email"
                  required
                  maxLength={254}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="w-full pr-4 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-semibold text-slate-900 focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-600/10 outline-none transition-all"
                  style={{ paddingLeft: "2.75rem" }}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-slate-900 hover:bg-blue-700 text-white text-sm font-bold rounded-2xl flex items-center justify-center gap-2 shadow-lg shadow-slate-950/10 transition-all disabled:opacity-60 cursor-pointer"
            >
              {loading ? (
                "Sending Code…"
              ) : (
                <>
                  <span>Send Login Code</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOtp} noValidate className="space-y-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-700 ml-1">
                6-Digit Code
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                  <KeyRound className="w-5 h-5" />
                </div>
                <input
                  type="text"
                  maxLength={6}
                  required
                  autoFocus
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  placeholder="123456"
                  className="w-full pr-4 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-center text-lg font-black tracking-widest text-slate-900 focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-600/10 outline-none transition-all"
                />
              </div>
            </div>

            <div className="flex items-center justify-between text-xs font-semibold px-1">
              <button
                type="button"
                onClick={() => {
                  setStatusMessage(null);
                  setStep("REQUEST");
                }}
                className="text-slate-500 hover:text-slate-800 underline cursor-pointer"
              >
                Change email
              </button>

              <button
                type="button"
                disabled={cooldown > 0 || loading}
                onClick={handleSendOtp}
                className="text-blue-600 hover:text-blue-700 disabled:text-slate-400 flex items-center gap-1 font-bold cursor-pointer"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                <span>{cooldown > 0 ? `Resend in ${cooldown}s` : "Resend Code"}</span>
              </button>
            </div>

            <button
              type="submit"
              disabled={loading || otp.length !== 6}
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded-2xl flex items-center justify-center gap-2 shadow-lg shadow-blue-600/20 transition-all disabled:opacity-60 cursor-pointer"
            >
              {loading ? (
                "Verifying…"
              ) : (
                <>
                  <span>Verify & Log In</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}